import React, { useEffect } from 'react';
import { WelcomePageLayout } from '@/components/cross-entity/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, Vote, TrendingUp, Star } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';
import { Badge } from '@/components/ui/badge';

export const GoldWelcome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ensure page starts at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContinue = () => {
    // Track analytics
    pricingAnalytics.trackTierSelection('gold', 'gold_welcome_page');

    // Preserve add-ons from URL and navigate to assessment
    const addonsParam = searchParams.get('addons');
    const assessmentUrl = addonsParam
      ? `/onboarding/assessment?tier=gold&addons=${encodeURIComponent(addonsParam)}`
      : '/onboarding/assessment?tier=gold';

    // Use direct navigation for reliability
    window.location.href = assessmentUrl;
  };

  return (
    <WelcomePageLayout tier="gold" onContinue={handleContinue}>
      <div className="space-y-8">
        {/* Most Popular Badge */}
        <div className="text-center">
          <Badge className="bg-blue-500 text-white px-4 py-2 text-lg font-semibold mb-4">
            <Star className="w-5 h-5 mr-2" />
            Most Popular Choice
          </Badge>
        </div>

        {/* What's Next Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Excellent Choice! Let's Transform Your Community
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            The Gold Package includes everything in Silver PLUS advanced treasury tools and workflow
            automation to help your community operate more efficiently.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="text-center border-blue-200">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 font-bold text-xl">1</span>
              </div>
              <CardTitle className="text-lg">Comprehensive Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Deep dive into your community's financial position, assets, and growth potential
              </p>
            </CardContent>
          </Card>

          <Card className="text-center border-blue-200">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-purple-600 font-bold text-xl">2</span>
              </div>
              <CardTitle className="text-lg">Wealth Acceleration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Coordinate community projects and track finances with advanced tools
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Gold Package Exclusive Features */}
        <div className="bg-linear-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Gold Package: Everything in Silver PLUS
          </h3>
          <div className="text-center mb-6">
            <Badge className="bg-linear-to-r from-purple-500 to-blue-500 text-white">
              EXCLUSIVE FEATURES
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Treasury Tracking</h4>
                <p className="text-sm text-gray-600">
                  Track your community's reserves and spending with detailed, board-ready reporting
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Vote className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Investment Proposal Voting</h4>
                <p className="text-sm text-gray-600">
                  Community members vote on investment and spending proposals before funds are
                  committed
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-orange-100 rounded-full p-3">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Optimized Treasury Strategies</h4>
                <p className="text-sm text-gray-600">
                  Smarter treasury strategies that maximize returns while maintaining liquidity
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Special Gold Benefits */}
        <div className="bg-linear-to-r from-yellow-50 to-orange-50 border border-orange-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Star className="w-5 h-5 text-orange-500" />
            Gold Package Benefits:
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              • <strong>Priority Support:</strong> Dedicated account manager and faster response
              times
            </li>
            <li>
              • <strong>Revenue Generation:</strong> Tools to track and report on community
              investment proposals
            </li>
            <li>
              • <strong>Risk Mitigation:</strong> Professional financial guidance and transparent
              treasury reporting
            </li>
            <li>
              • <strong>Scalable Growth:</strong> Infrastructure for major community improvements
              and expansion
            </li>
            <li>
              • <strong>Competitive Advantage:</strong> Advanced treasury and voting tools built for
              community governance
            </li>
          </ul>
        </div>

        {/* Expectations */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              • <strong>Financial Assessment:</strong> We'll analyze your community's current
              financial position
            </li>
            <li>
              • <strong>Treasury Setup:</strong> Configure treasury tracking and reporting for your
              community
            </li>
            <li>
              • <strong>Investment Strategy:</strong> Custom financial planning workflow for your
              community
            </li>
            <li>
              • <strong>Implementation:</strong> 6-8 week rollout with dedicated Gold package
              support
            </li>
            <li>
              • <strong>Ongoing Success:</strong> Quarterly reviews and optimization recommendations
            </li>
          </ul>
        </div>
      </div>
    </WelcomePageLayout>
  );
};
